import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useNavigate } from 'react-router-dom';

const ServicePageTemplate = ({ icon: Icon, title, subtitle, description, features, benefits, gradient = 'from-emerald-500 to-green-600' }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className={`inline-flex w-20 h-20 bg-gradient-to-br ${gradient} rounded-2xl items-center justify-center mb-6 shadow-2xl`}>
              <Icon className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4" data-testid="page-title">
              {title}
            </h1>
            <p className="text-xl text-green-50 leading-relaxed">{subtitle}</p>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-gray-700 leading-relaxed mb-12">{description}</p>

            {features && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">What's Included</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-4 bg-white border-2 border-gray-100 rounded-xl hover:border-emerald-300 hover:shadow-lg transition-all">
                      <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {benefits && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Benefits</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {benefits.map((benefit, idx) => (
                    <Card key={idx} className="border-2 border-gray-100 hover:border-emerald-300 transition-all hover:shadow-xl hover:-translate-y-1">
                      <CardContent className="p-6">
                        <div className={`w-12 h-12 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center mb-4`}>
                          <span className="text-white font-bold">{idx + 1}</span>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                        <p className="text-gray-600 text-sm">{benefit.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-green-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-green-50 mb-8 max-w-2xl mx-auto">Let's discuss how we can help you achieve your fundraising goals.</p>
          <Button size="lg" onClick={() => navigate('/contact')}
            className="bg-white text-emerald-700 hover:bg-green-50 text-lg px-12 py-6 shadow-2xl hover:shadow-white/50 transition-all duration-300 hover:scale-105 font-bold">
            Connect With Us<ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ServicePageTemplate;
